import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { authenticatedFetch, AuthFetchError } from '../utils/authenticatedFetch';
import { pdfCache } from '../services/pdfCacheService';
import { highlightOnCanvas as highlightOnCanvasUtil } from '../utils/highlightOnCanvas';
import { normalize as textMatchNormalize, lcsRatio as textMatchLcsRatio } from '../utils/textMatchUtils';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { usePdfRedaction } from '../hooks/usePdfRedaction';
import type { PiiRect } from '../hooks/usePdfRedaction';


// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface PageDimension {
  width: number;
  height: number;
}

interface PdfRendererProps {
  /** URL to fetch the PDF from (authenticated via stored token). */
  documentUrl: string;
  /** Document ID for fetching line geometry (used by PDF redaction). */
  documentId?: string;
  /** 1-indexed target page to scroll to. */
  page: number;
  /** Zoom scale (e.g. 1.5 = 150%). */
  scale: number;
  /** Text to search for and highlight on the target page. */
  searchText?: string;
  /** Called once when PDF finishes loading. */
  onLoadComplete?: (totalPages: number) => void;
  /** Called when an error occurs. */
  onError?: (error: string) => void;
  /** Called when searchText is found on a different page than `page`. */
  onPageCorrection?: (correctPage: number) => void;
  /** Called when the visible page changes due to user scrolling. */
  onCurrentPageChange?: (page: number) => void;
  /** Called after attempting to highlight searchText — false means text was not found in the PDF layer. Score is 0-1 (1.0 = exact prefix match). */
  onHighlightResult?: (found: boolean, matchScore?: number) => void;
  /** Externally-provided bounding box to highlight (normalized 0-1 coords, for scanned pages). */
  highlightBox?: { x: number; y: number; width: number; height: number };
  /** Pre-fetched PDF data from the cache.  When provided, skips the network fetch. */
  cachedData?: ArrayBuffer;
  /** When true, the user can draw a rectangle to zoom into a region. */
  areaZoomActive?: boolean;
  /** Called when the user completes an area-zoom selection with the new scale. PdfRenderer handles scrolling internally. */
  onAreaZoom?: (newScale: number) => void;
  /** Called when the user clicks (without dragging) on the document while zoomed, to reset zoom. */
  onAreaZoomReset?: () => void;
  /** Per-page rotation overrides in degrees (0, 90, 180, 270). Key = 1-indexed page number. */
  pageRotations?: Record<number, number>;
  /** Called after load with suggested per-page rotations for pages where text runs vertically. */
  onAutoRotateDetected?: (rotations: Record<number, number>) => void;
  /** CSS filter string to apply to page containers (brightness, contrast, etc.). */
  cssFilterStyle?: string;
  /** Additional class names for the outer scroll container. */
  className?: string;
}

const PAGE_GAP = 16; // px between pages

export const PdfRenderer: React.FC<PdfRendererProps> = ({
  documentUrl,
  documentId,
  page,
  scale,
  searchText,
  onLoadComplete,
  onError,
  onPageCorrection,
  onCurrentPageChange,
  onHighlightResult,
  highlightBox,
  cachedData,
  areaZoomActive = false,
  onAreaZoom,
  onAreaZoomReset,
  pageRotations,
  onAutoRotateDetected,
  cssFilterStyle,
  className = '',
}) => {
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [pageDimensions, setPageDimensions] = useState<PageDimension[]>([]);
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());

  // Redaction mode integration
  const { isEnabled: redactionEnabled, sourceDocsRevealed } = useRedactionMode();
  const { piiRectsPerPage } = usePdfRedaction({
    documentId: documentId ?? null,
    visiblePages,
    totalPages: pageDimensions.length,
    pdfDocument,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const innerColumnRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const pageSlotRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightCanvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const renderTasksRef = useRef<Map<number, any>>(new Map());
  const visiblePagesRef = useRef<Set<number>>(new Set());
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const programmaticScrollRef = useRef(false);
  const suppressObserverUntilRef = useRef(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReportedPageRef = useRef(page);
  const targetPageRef = useRef(page);
  targetPageRef.current = page;
  const prevPageRef = useRef(page);
  const prevScaleRef = useRef(scale);
  const visiblePagesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks which page indices have real (non-estimated) dimensions. Pages
  // beyond the first INITIAL_DIM_BATCH start with an average estimate; the
  // centring math is wrong against estimates, so we pre-fetch real dims for
  // any page we need to scroll to.
  const realDimsRef = useRef<Set<number>>(new Set());

  // ── Area-zoom selection state ──────────────────────────────────────────────
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number; pageNum: number } | null>(null);
  const isDraggingRef = useRef(false);
  const selectionStartRef = useRef<{ x: number; y: number; pageNum: number } | null>(null);
  // Stores the normalised selection region so we can scroll to it after the scale change re-renders
  const pendingAreaScrollRef = useRef<{ pageNum: number; normLeft: number; normTop: number } | null>(null);

  const handleSelectionMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!areaZoomActive) return;
    const container = containerRef.current;
    if (!container) return;

    const target = e.target as HTMLElement;
    const pageSlot = target.closest('[data-page]') as HTMLElement | null;
    if (!pageSlot) return;
    const pageNum = Number(pageSlot.getAttribute('data-page'));
    if (!pageNum) return;

    const slotRect = pageSlot.getBoundingClientRect();
    const x = e.clientX - slotRect.left;
    const y = e.clientY - slotRect.top;

    selectionStartRef.current = { x, y, pageNum };
    isDraggingRef.current = true;
    setSelectionRect({ startX: x, startY: y, endX: x, endY: y, pageNum });
    e.preventDefault();
  }, [areaZoomActive]);

  const handleSelectionMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !selectionStartRef.current) return;
    const pageSlot = pageSlotRefs.current.get(selectionStartRef.current.pageNum);
    if (!pageSlot) return;

    const slotRect = pageSlot.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - slotRect.left, slotRect.width));
    const y = Math.max(0, Math.min(e.clientY - slotRect.top, slotRect.height));

    setSelectionRect(prev => prev ? { ...prev, endX: x, endY: y } : null);
  }, []);

  const handleSelectionMouseUp = useCallback(() => {
    if (!isDraggingRef.current || !selectionRect || !onAreaZoom) {
      isDraggingRef.current = false;
      setSelectionRect(null);
      return;
    }
    isDraggingRef.current = false;

    const { startX, startY, endX, endY, pageNum } = selectionRect;
    const left = Math.min(startX, endX);
    const top = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    // Tiny selection = a click, not a drag — reset zoom if zoomed in
    if (width < 20 || height < 20) {
      setSelectionRect(null);
      if (onAreaZoomReset) onAreaZoomReset();
      return;
    }

    const container = containerRef.current;
    const dim = pageDimensions[pageNum - 1];
    if (!container || !dim) {
      setSelectionRect(null);
      return;
    }

    const containerWidth = container.clientWidth - 32;
    const containerHeight = container.clientHeight - 32;
    const scaleForWidth = (containerWidth / width) * scale;
    const scaleForHeight = (containerHeight / height) * scale;
    const newScale = Math.min(Math.round(Math.min(scaleForWidth, scaleForHeight) * 100) / 100, 5.0);

    // Store normalised position so we can scroll after re-render at new scale
    pendingAreaScrollRef.current = {
      pageNum,
      normLeft: left / dim.width,
      normTop: top / dim.height,
    };

    setSelectionRect(null);
    onAreaZoom(newScale);
  }, [selectionRect, onAreaZoom, onAreaZoomReset, scale, pageDimensions]);

  // After pageDimensions update (triggered by scale change), apply the pending area-zoom scroll
  useEffect(() => {
    const pending = pendingAreaScrollRef.current;
    if (!pending || pageDimensions.length === 0) return;
    pendingAreaScrollRef.current = null;

    requestAnimationFrame(() => {
      const container = containerRef.current;
      const slot = pageSlotRefs.current.get(pending.pageNum);
      const dim = pageDimensions[pending.pageNum - 1];
      if (!container || !slot || !dim) return;

      programmaticScrollRef.current = true;
      suppressObserverUntilRef.current = Date.now() + 1000;

      const scrollTop = slot.offsetTop + pending.normTop * dim.height - 16;
      const slotLeft = slot.offsetLeft;
      const scrollLeft = slotLeft + pending.normLeft * dim.width - 16;

      container.scrollTo({ top: Math.max(0, scrollTop), left: Math.max(0, scrollLeft), behavior: 'instant' });

      setTimeout(() => { programmaticScrollRef.current = false; }, 500);
    });
  }, [pageDimensions]);

  // Guard: track which highlight we've already scrolled to, preventing repeat
  // scrolls that snap the user back when renderPage re-fires.
  const scrolledToHighlightRef = useRef<string | null>(null);

  // Stable refs for callback props so renderPage doesn't get a new identity
  // every time the parent re-renders with a new inline callback.
  const onHighlightResultRef = useRef(onHighlightResult);
  const onPageCorrectionRef = useRef(onPageCorrection);
  const pageRotationsRef = useRef(pageRotations);
  useEffect(() => { onHighlightResultRef.current = onHighlightResult; }, [onHighlightResult]);
  useEffect(() => { onPageCorrectionRef.current = onPageCorrection; }, [onPageCorrection]);
  useEffect(() => { pageRotationsRef.current = pageRotations; }, [pageRotations]);

  const onAutoRotateDetectedRef = useRef(onAutoRotateDetected);
  useEffect(() => { onAutoRotateDetectedRef.current = onAutoRotateDetected; }, [onAutoRotateDetected]);



  // ── Load PDF ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    scrolledToHighlightRef.current = null;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        setDownloadProgress(null);
        setPageDimensions([]);

        let arrayBuffer: ArrayBuffer;
        if (cachedData) {
          // Use pre-fetched data from the PDF cache — skip network entirely
          arrayBuffer = cachedData;
        } else if (documentId) {
          // Use cache service with streaming progress
          const isPreview = documentUrl.includes('?preview=true');
          arrayBuffer = await pdfCache.fetchAndCache(documentId, isPreview, (fraction) => {
            setDownloadProgress(fraction);
          });
        } else {
          const response = await authenticatedFetch(documentUrl);
          if (response.status === 204) {
            throw new AuthFetchError(204);
          }
          arrayBuffer = await (await response.blob()).arrayBuffer();
        }

        // pdf.js transfers (detaches) the ArrayBuffer, so pass a copy to
        // keep the cached original valid for future loads.
        // Pass ownerDocument so pdf.js loads fonts into the correct document
        // (critical when rendered inside a popup window via createPortal).
        const targetDoc = containerRef.current?.ownerDocument;
        const pdf = await pdfjsLib.getDocument({
          data: arrayBuffer.slice(0),
          ...(targetDoc && targetDoc !== document ? { ownerDocument: targetDoc } : {}),
        }).promise;

        if (cancelled) {
          pdf.destroy();
          return;
        }

        pdfDocRef.current?.destroy();
        pdfDocRef.current = pdf;
        setPdfDocument(pdf);
        onLoadComplete?.(pdf.numPages);
        setLoading(false);
        setDownloadProgress(null);

        // Auto-rotate detection is now deferred to visible pages (see
        // detectAutoRotateForPages below) — no upfront full-document scan.
      } catch (err) {
        if (!cancelled) {
          // If the cached buffer failed to parse as a PDF, evict the entry so
          // a retry actually refetches. Without this, a historically-poisoned
          // cache entry (e.g. a truncated stream from a cancelled navigation)
          // would re-serve the same bad bytes forever until logout.
          const errName = (err as { name?: string } | undefined)?.name;
          if (documentId && (errName === 'InvalidPDFException' || errName === 'MissingPDFException')) {
            pdfCache.evict(documentId);
          }

          let msg: string;
          // Browser reports offline → almost always the user's connection.
          // Check this first because an offline fetch may surface as a 5xx
          // from a flaky upstream rather than a clean TypeError.
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            msg = 'You appear to be offline. Check your connection and try again.';
          } else if (err instanceof AuthFetchError) {
            switch (err.status) {
              case 204: msg = 'No preview available for this document.'; break;
              case 401: msg = 'Session expired. Please refresh the page to log in again.'; break;
              case 403: msg = 'You don\'t have permission to view this document.'; break;
              case 404: msg = 'Document not found. It may have been deleted.'; break;
              default:
                if (err.status >= 500) {
                  // 5xx is often a transient gateway/upstream timeout — these
                  // commonly correlate with the user's own connection wobbling.
                  msg = "Couldn't load this document right now. This may be a temporary connection issue — please try again.";
                } else {
                  msg = `Failed to load document (error ${err.status}). Please try again.`;
                }
            }
          } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
            msg = 'Network error. Check your connection and try again.';
          } else {
            msg = 'Failed to load document. Please try again.';
          }
          console.error('[PdfRenderer] Load error:', msg, err);
          setError(msg);
          onError?.(msg);
          setLoading(false);
        }
      }
    };

    loadPDF();
    return () => { cancelled = true; };
  }, [documentUrl, cachedData, retryCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      renderTasksRef.current.forEach((task) => { try { task.cancel(); } catch {} });
      renderTasksRef.current.clear();
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
    };
  }, []);

  // Stable serialised key so rotation changes trigger dimension updates
  const rotationKey = pageRotations
    ? Object.entries(pageRotations).sort(([a], [b]) => +a - +b).map(([k, v]) => `${k}:${v}`).join(',')
    : '';
  const prevRotationKeyRef = useRef(rotationKey);

  // ── Compute page dimensions incrementally ──────────────────────────────────
  // For large documents (1000+ pages), computing all dimensions upfront
  // blocks rendering for seconds. Instead, compute the first batch immediately
  // (enough to fill the viewport and allow scrolling), then backfill the rest
  // in idle callbacks so the UI stays responsive.
  const INITIAL_DIM_BATCH = 30;

  useEffect(() => {
    if (!pdfDocument) return;
    let cancelled = false;

    const isRotationOnlyChange = prevRotationKeyRef.current !== rotationKey
      && pageDimensions.length === pdfDocument.numPages;
    prevRotationKeyRef.current = rotationKey;

    // ── Rotation-only change: surgically update only affected pages ────────
    // Avoids wiping realDimsRef and re-estimating hundreds of pages, which
    // causes layout shifts that break scroll positioning.
    if (isRotationOnlyChange) {
      const rots = pageRotationsRef.current;
      // Only process pages that actually have a rotation defined — pages
      // without rotation keep rotation 0, matching their existing dimensions.
      const rotatedPages = rots ? Object.keys(rots).map(Number).filter(n => n >= 1 && n <= pdfDocument.numPages) : [];

      if (rotatedPages.length === 0) return;

      const updateRotatedPages = async () => {
        const updates: Array<{ idx: number; dim: PageDimension }> = [];

        for (const i of rotatedPages) {
          if (cancelled) return;
          const rotation = rots![i] ?? 0;
          try {
            const pdfPage = await pdfDocument.getPage(i);
            const viewport = pdfPage.getViewport({ scale, rotation });
            const real = { width: viewport.width, height: viewport.height };
            const existing = pageDimensions[i - 1];
            if (existing && (existing.width !== real.width || existing.height !== real.height)) {
              updates.push({ idx: i - 1, dim: real });
            }
            realDimsRef.current.add(i);
          } catch { /* ignore */ }
        }
        if (cancelled || updates.length === 0) return;
        setPageDimensions(prev => {
          const next = prev.slice();
          for (const { idx, dim } of updates) next[idx] = dim;
          return next;
        });

        // Re-render any rotated pages that are currently visible so the
        // canvas reflects the new rotation immediately.
        for (const i of rotatedPages) {
          if (visiblePagesRef.current.has(i)) {
            renderedPagesRef.current.delete(i);
            renderPage(i);
          }
        }
      };
      updateRotatedPages();
      return () => { cancelled = true; };
    }

    // ── Full recomputation (new document or scale change) ─────────────────
    realDimsRef.current = new Set();

    const computeDimensions = async () => {
      const totalPages = pdfDocument.numPages;
      const rots = pageRotationsRef.current;

      // Phase 1: compute first batch for immediate render
      const firstBatchSize = Math.min(INITIAL_DIM_BATCH, totalPages);
      const dims: PageDimension[] = [];
      for (let i = 1; i <= firstBatchSize; i++) {
        const pdfPage = await pdfDocument.getPage(i);
        const rotation = rots?.[i] ?? 0;
        const viewport = pdfPage.getViewport({ scale, rotation });
        dims.push({ width: viewport.width, height: viewport.height });
        realDimsRef.current.add(i);
      }

      if (cancelled) return;

      if (totalPages <= INITIAL_DIM_BATCH) {
        setPageDimensions(dims);
        return;
      }

      // For remaining pages, use the average of the first batch as an estimate
      const avgWidth = dims.reduce((s, d) => s + d.width, 0) / dims.length;
      const avgHeight = dims.reduce((s, d) => s + d.height, 0) / dims.length;
      const estimated: PageDimension[] = new Array(totalPages);
      for (let i = 0; i < firstBatchSize; i++) estimated[i] = dims[i];
      for (let i = firstBatchSize; i < totalPages; i++) {
        estimated[i] = { width: avgWidth, height: avgHeight };
      }
      setPageDimensions([...estimated]);

      // Phase 2: backfill real dimensions in batches during idle time
      const BACKFILL_BATCH = 50;
      let cursor = firstBatchSize + 1;

      const backfillBatch = async () => {
        if (cancelled || cursor > totalPages) return;
        const end = Math.min(cursor + BACKFILL_BATCH - 1, totalPages);
        let changed = false;

        for (let i = cursor; i <= end; i++) {
          if (cancelled) return;
          if (realDimsRef.current.has(i)) continue;
          try {
            const pdfPage = await pdfDocument.getPage(i);
            const rotation = rots?.[i] ?? 0;
            const viewport = pdfPage.getViewport({ scale, rotation });
            const real = { width: viewport.width, height: viewport.height };
            if (real.width !== estimated[i - 1].width || real.height !== estimated[i - 1].height) {
              estimated[i - 1] = real;
              changed = true;
            }
            realDimsRef.current.add(i);
          } catch { /* ignore */ }
        }

        cursor = end + 1;

        if (changed && !cancelled) {
          setPageDimensions([...estimated]);
        }

        if (cursor <= totalPages && !cancelled) {
          const schedule = typeof requestIdleCallback === 'function'
            ? requestIdleCallback
            : (cb: () => void) => setTimeout(cb, 50);
          schedule(() => { backfillBatch(); });
        }
      };

      const schedule = typeof requestIdleCallback === 'function'
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 50);
      schedule(() => { backfillBatch(); });
    };

    computeDimensions();
    return () => { cancelled = true; };
  }, [pdfDocument, scale, rotationKey]);

  // ── Pre-fetch real dimensions for a specific page on demand ────────────────
  // Centring math is wrong against estimated dimensions. Before scrolling to
  // a target page we ensure its real viewport size has been measured and
  // committed to pageDimensions, so the slot's CSS width is accurate.
  const ensureRealDimension = useCallback(async (pageNum: number): Promise<void> => {
    if (!pdfDocument) return;
    if (pageNum < 1 || pageNum > pdfDocument.numPages) return;
    if (realDimsRef.current.has(pageNum)) return;

    try {
      const pdfPage = await pdfDocument.getPage(pageNum);
      const rotation = pageRotationsRef.current?.[pageNum] ?? 0;
      const viewport = pdfPage.getViewport({ scale, rotation });
      const real = { width: viewport.width, height: viewport.height };
      realDimsRef.current.add(pageNum);

      setPageDimensions(prev => {
        const existing = prev[pageNum - 1];
        if (!existing) return prev;
        if (existing.width === real.width && existing.height === real.height) return prev;
        const next = prev.slice();
        next[pageNum - 1] = real;
        return next;
      });
    } catch { /* ignore — fall back to estimate */ }
  }, [pdfDocument, scale]);

  // ── Bulk-fetch real dimensions for all pages up to a target ──────────────
  // For large jumps, slot.offsetTop accumulates estimation errors from every
  // preceding page. This fetches real viewports for all unmeasured pages up
  // to (and including) the target so the offset is accurate before we scroll.
  const ensureRealDimensionsUpTo = useCallback(async (upTo: number): Promise<void> => {
    if (!pdfDocument) return;
    const total = pdfDocument.numPages;
    const end = Math.min(upTo, total);

    // Collect pages that still need real dimensions
    const needed: number[] = [];
    for (let i = 1; i <= end; i++) {
      if (!realDimsRef.current.has(i)) needed.push(i);
    }
    if (needed.length === 0) return;

    // Batch-fetch in chunks to avoid blocking the main thread for too long
    const CHUNK = 100;
    for (let c = 0; c < needed.length; c += CHUNK) {
      const chunk = needed.slice(c, c + CHUNK);
      const updates: Array<{ idx: number; dim: PageDimension }> = [];

      for (const i of chunk) {
        if (realDimsRef.current.has(i)) continue;
        try {
          const pdfPage = await pdfDocument.getPage(i);
          const rotation = pageRotationsRef.current?.[i] ?? 0;
          const viewport = pdfPage.getViewport({ scale, rotation });
          const real = { width: viewport.width, height: viewport.height };
          realDimsRef.current.add(i);
          updates.push({ idx: i - 1, dim: real });
        } catch { /* ignore */ }
      }

      if (updates.length > 0) {
        setPageDimensions(prev => {
          let changed = false;
          const next = prev.slice();
          for (const { idx, dim } of updates) {
            const existing = next[idx];
            if (existing && (existing.width !== dim.width || existing.height !== dim.height)) {
              next[idx] = dim;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }

      // Yield to the browser between chunks
      if (c + CHUNK < needed.length) {
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      }
    }
  }, [pdfDocument, scale]);

  // ── Lazy auto-rotate detection (per-page, on visibility) ────────────────
  // Instead of scanning every page upfront (which crashed the browser on
  // 1000+ page documents), detect rotation only when a page becomes visible.
  const autoRotateScannedRef = useRef<Set<number>>(new Set());
  // Pages whose auto-rotate detection was deferred because a programmatic
  // scroll was in progress.  Flushed when the scroll settles.
  const deferredAutoRotateRef = useRef<Set<number>>(new Set());

  const detectAutoRotateForPages = useCallback(async (pages: Set<number>) => {
    const pdf = pdfDocRef.current;
    const callback = onAutoRotateDetectedRef.current;
    if (!pdf || !callback) return;

    const detected: Record<number, number> = {};

    for (const i of pages) {
      if (autoRotateScannedRef.current.has(i)) continue;
      autoRotateScannedRef.current.add(i);

      try {
        const p = await pdf.getPage(i);

        // Signal 1: text-matrix orientation
        const tc = await p.getTextContent();
        let horizontal = 0, vUp = 0, vDown = 0;
        for (const item of tc.items) {
          if (!('transform' in item) || !('str' in item)) continue;
          const str = (item as any).str as string;
          if (!str.trim()) continue;
          const t = (item as any).transform as number[];
          const a = t[0], b = t[1];
          const len = str.length;
          if (Math.abs(a) >= Math.abs(b) * 1.5) {
            horizontal += len;
          } else if (b > 0) {
            vUp += len;
          } else {
            vDown += len;
          }
        }
        const textTotal = horizontal + vUp + vDown;

        let suggestion: number | null = null;

        if (textTotal >= 20 && (vUp + vDown) / textTotal >= 0.7) {
          suggestion = vUp >= vDown ? 270 : 90;
        } else {
          // Signal 2: image CTM orientation
          try {
            const ops = await p.getOperatorList();
            const OPS = (pdfjsLib as any).OPS;
            const stack: number[][] = [];
            let ctm: number[] = [1, 0, 0, 1, 0, 0];
            const imageOps = new Set<number>([
              OPS.paintImageXObject,
              OPS.paintJpegXObject,
              OPS.paintImageXObjectRepeat,
              OPS.paintInlineImageXObject,
              OPS.paintImageMaskXObject,
            ].filter((v) => typeof v === 'number'));
            let foundCtm: number[] | null = null;
            for (let k = 0; k < ops.fnArray.length; k++) {
              const fn = ops.fnArray[k];
              const args = ops.argsArray[k];
              if (fn === OPS.save) {
                stack.push([...ctm]);
              } else if (fn === OPS.restore) {
                ctm = stack.pop() ?? [1, 0, 0, 1, 0, 0];
              } else if (fn === OPS.transform) {
                const [a, b, c, d, e, f] = args as number[];
                const [m0, m1, m2, m3, m4, m5] = ctm;
                ctm = [
                  m0 * a + m2 * b,
                  m1 * a + m3 * b,
                  m0 * c + m2 * d,
                  m1 * c + m3 * d,
                  m0 * e + m2 * f + m4,
                  m1 * e + m3 * f + m5,
                ];
              } else if (imageOps.has(fn)) {
                foundCtm = [...ctm];
                break;
              }
            }

            if (foundCtm) {
              const [a, b, , d] = foundCtm;
              const horizontalAxis = Math.abs(a) >= Math.abs(b);
              if (horizontalAxis) {
                suggestion = (a < 0 && d < 0) ? 180 : 0;
              } else if (b > 0) {
                suggestion = 90;
              } else {
                suggestion = 270;
              }
            }
          } catch { /* ignore */ }
        }

        if (suggestion && suggestion !== 0) {
          detected[i] = suggestion;
        }
      } catch { /* ignore */ }
    }

    if (Object.keys(detected).length > 0) {
      callback(detected);
    }
  }, []);

  // Reset auto-rotate scan set when document changes
  useEffect(() => {
    autoRotateScannedRef.current = new Set();
  }, [documentUrl]);

  // ── Render a single page onto its canvas ───────────────────────────────────
  const renderPage = useCallback(async (pageNum: number) => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRefs.current.get(pageNum);
    if (!pdf || !canvas) return;

    // Cancel any existing render for this page
    const existing = renderTasksRef.current.get(pageNum);
    if (existing) {
      try { existing.cancel(); } catch {}
      renderTasksRef.current.delete(pageNum);
    }

    try {
      const pdfPage = await pdf.getPage(pageNum);
      const rotation = pageRotationsRef.current?.[pageNum] ?? 0;
      const viewport = pdfPage.getViewport({ scale, rotation });

      // Render at 2× resolution for sharp text, display at 1× via CSS
      const dpr = 2;
      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const renderViewport = pdfPage.getViewport({ scale: scale * dpr, rotation });

      const task = pdfPage.render({
        canvasContext: canvas.getContext('2d')!,
        viewport: renderViewport,
        canvas,
      } as any);
      renderTasksRef.current.set(pageNum, task);

      await task.promise;
      renderTasksRef.current.delete(pageNum);
      renderedPagesRef.current.add(pageNum);

      // Clear the highlight overlay canvas before drawing any new highlights.
      // This ensures stale text-layer highlights are removed when searchText
      // becomes undefined (e.g. Textract geometry matched and took priority).
      const highlightCanvas = highlightCanvasRefs.current.get(pageNum);
      if (highlightCanvas) {
        highlightCanvas.width = viewport.width;
        highlightCanvas.height = viewport.height;
      }

      // Apply search highlighting if this is the target page
      if (searchText && pageNum === page) {
        if (highlightCanvas) {
          const textContent = await pdfPage.getTextContent();
          const { found, matchScore } = highlightOnCanvas(highlightCanvas, textContent, viewport, searchText);
          onHighlightResultRef.current?.(found, matchScore);

          if (!found && onPageCorrectionRef.current) {
            tryAdjacentPages(pdf, pageNum, searchText);
          }
        }
      }

      // Draw externally-provided bounding box highlight (for scanned pages)
      if (highlightBox && pageNum === page) {
        if (highlightCanvas) {
          const ctx = highlightCanvas.getContext('2d')!;
          ctx.fillStyle = 'rgba(255, 255, 0, 0.35)';
          ctx.fillRect(
            highlightBox.x * viewport.width,
            highlightBox.y * viewport.height,
            highlightBox.width * viewport.width,
            highlightBox.height * viewport.height,
          );

          // Scroll to the highlight (only once per unique highlight).
          // Clamp so we never scroll above the page slot — otherwise the
          // observer sees the previous page as topmost and reports a wrong page.
          const boxKey = `box:${pageNum}:${highlightBox.x}:${highlightBox.y}`;
          const slot = pageSlotRefs.current.get(page);
          if (slot && containerRef.current && scrolledToHighlightRef.current !== boxKey) {
            scrolledToHighlightRef.current = boxKey;
            const hlY = highlightBox.y * viewport.height;
            const targetScroll = Math.max(slot.offsetTop, slot.offsetTop + hlY - 200);
            // Scroll to the highlight position within the page. Use instant
            // scroll — smooth animation is unnecessary for a sub-page offset
            // and can conflict with an in-flight page navigation scroll.
            setTimeout(() => {
              const c = containerRef.current;
              const s = pageSlotRefs.current.get(page);
              if (!c) return;
              let centredLeft = c.scrollLeft;
              if (s && c.scrollWidth > c.clientWidth) {
                centredLeft = Math.max(0, s.offsetLeft + s.offsetWidth / 2 - c.clientWidth / 2);
              }
              c.scrollTo({
                top: targetScroll,
                left: centredLeft,
                behavior: 'instant',
              });
            }, 50);
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'RenderingCancelledException') return;
    }
  }, [scale, searchText, page, highlightBox]);

  // ── Clear a page canvas when it leaves the viewport ────────────────────────
  const clearPage = useCallback((pageNum: number) => {
    const existing = renderTasksRef.current.get(pageNum);
    if (existing) {
      try { existing.cancel(); } catch {}
      renderTasksRef.current.delete(pageNum);
    }
    renderedPagesRef.current.delete(pageNum);

    const canvas = canvasRefs.current.get(pageNum);
    if (canvas) { canvas.width = 0; canvas.height = 0; }

    const highlight = highlightCanvasRefs.current.get(pageNum);
    if (highlight) { highlight.width = 0; highlight.height = 0; }
  }, []);

  // ── Centre the horizontal scroll on a given page slot ─────────────────────
  // Wide pages (e.g. scanned images) can overflow the container.  Without
  // this the container defaults to scrollLeft = 0 and the user sees blank
  // canvas on the left while the actual content is off-screen to the right.
  //
  // Returns the computed scrollLeft so callers can pass it to scrollTo for
  // smooth animation, or null if the page does not overflow horizontally.
  const computeCentreScrollLeft = useCallback((pageNum: number): number | null => {
    const container = containerRef.current;
    const slot = pageSlotRefs.current.get(pageNum);
    if (!container || !slot) return null;
    if (container.scrollWidth <= container.clientWidth) return null; // nothing to centre

    const slotLeft = slot.offsetLeft;
    const slotWidth = slot.offsetWidth;
    const idealScrollLeft = slotLeft + slotWidth / 2 - container.clientWidth / 2;
    return Math.max(0, idealScrollLeft);
  }, []);

  const centreHorizontallyOn = useCallback((pageNum: number) => {
    const container = containerRef.current;
    const target = computeCentreScrollLeft(pageNum);
    if (!container || target === null) return;
    container.scrollLeft = target;
  }, [computeCentreScrollLeft]);

  // ── IntersectionObserver for lazy rendering ────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || pageDimensions.length === 0 || !pdfDocument) return;

    const renderObserver = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          const pageNum = Number(entry.target.getAttribute('data-page'));
          if (!pageNum) continue;

          if (entry.isIntersecting) {
            if (!visiblePagesRef.current.has(pageNum)) {
              visiblePagesRef.current.add(pageNum);
              changed = true;
            }
          } else {
            if (visiblePagesRef.current.has(pageNum)) {
              visiblePagesRef.current.delete(pageNum);
              clearPage(pageNum);
            }
          }
        }
        if (changed) {
          const newlyVisible = new Set<number>();
          visiblePagesRef.current.forEach((pn) => {
            if (!renderedPagesRef.current.has(pn)) {
              renderPage(pn);
              newlyVisible.add(pn);
            }
          });

          // Lazy auto-rotate: detect rotation for newly visible pages.
          // Skip during programmatic scroll — detection changes pageRotations
          // which triggers a full dimension recomputation, shifting slot offsets
          // and killing the in-flight smooth scroll.  Queue them for when the
          // scroll settles instead.
          if (newlyVisible.size > 0 && !programmaticScrollRef.current) {
            detectAutoRotateForPages(newlyVisible);
          } else if (newlyVisible.size > 0) {
            // Stash pages for deferred detection after scroll finishes
            newlyVisible.forEach(p => deferredAutoRotateRef.current.add(p));
          }

          // Debounced update for redaction hook (avoid churn during fast scroll)
          if (visiblePagesDebounceRef.current) clearTimeout(visiblePagesDebounceRef.current);
          visiblePagesDebounceRef.current = setTimeout(() => {
            setVisiblePages(prev => {
              const next = visiblePagesRef.current;
              // Only create a new Set if contents actually changed
              if (prev.size === next.size && [...prev].every(p => next.has(p))) {
                return prev;
              }
              return new Set(next);
            });
          }, 100);
        }
      },
      { root: containerRef.current, rootMargin: '100% 0px' },
    );

    // Page-tracking observer for current page indicator
    const trackingObserver = new IntersectionObserver(
      (entries) => {
        // Use timestamp-based suppression so even newly-created observer
        // instances respect the suppression window (immune to effect re-runs).
        if (Date.now() < suppressObserverUntilRef.current) return;
        if (programmaticScrollRef.current) return;

        let topMostPage = lastReportedPageRef.current;
        let topMostY = Infinity;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pageNum = Number(entry.target.getAttribute('data-page'));
          if (!pageNum) continue;
          const rect = entry.boundingClientRect;
          if (rect.top < topMostY) {
            topMostY = rect.top;
            topMostPage = pageNum;
          }
        }

        if (topMostPage !== lastReportedPageRef.current) {
          lastReportedPageRef.current = topMostPage;
          onCurrentPageChange?.(topMostPage);
          centreHorizontallyOn(topMostPage);
          // User scrolled away from the sticky target — stop correcting
          stickyTargetRef.current = null;
        }
      },
      { root: containerRef.current, rootMargin: '0px 0px -50% 0px', threshold: 0 },
    );

    pageSlotRefs.current.forEach((el) => {
      renderObserver.observe(el);
      trackingObserver.observe(el);
    });

    return () => {
      renderObserver.disconnect();
      trackingObserver.disconnect();
    };
  }, [pageDimensions, pdfDocument, renderPage, clearPage, onCurrentPageChange, detectAutoRotateForPages, centreHorizontallyOn]);

  // ── Re-render visible pages when scale or searchText changes ───────────────
  useEffect(() => {
    if (pageDimensions.length === 0) return;

    // On scale change, preserve scroll position
    if (prevScaleRef.current !== scale && containerRef.current) {
      const scrollFraction = containerRef.current.scrollTop / (containerRef.current.scrollHeight || 1);
      prevScaleRef.current = scale;

      // Re-render after dimensions update
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = scrollFraction * containerRef.current.scrollHeight;
        }
        renderedPagesRef.current.clear();
        visiblePagesRef.current.forEach((pn) => renderPage(pn));
      });
    } else {
      // searchText change — re-render the target page for highlighting
      renderedPagesRef.current.clear();
      // Always re-render the target page (may not be in visiblePagesRef yet
      // due to observer teardown/re-creation race). Also re-render when
      // searchText becomes undefined (e.g. Textract geometry took over) so
      // stale text-layer highlights are cleared from the canvas.
      const pagesToRender = new Set(visiblePagesRef.current);
      if (page >= 1 && page <= pageDimensions.length) {
        pagesToRender.add(page);
      }
      pagesToRender.forEach((pn) => renderPage(pn));
    }
  }, [pageDimensions, scale, searchText, renderPage]);

  // ── Scroll to page when `page` prop or layout changes ─────────────────────
  // Track whether we've done a "big jump" to the current page so that if
  // pageDimensions change afterwards (rotation data arriving, backfill) we
  // silently re-scroll to keep the target page in view.
  const stickyTargetRef = useRef<number | null>(null);

  useEffect(() => {
    if (pageDimensions.length === 0) return;

    const container = containerRef.current;
    if (!container) return;

    // ── Dimension-change re-scroll: if we previously jumped to a page and
    // dimensions shifted (e.g. rotation data arrived), re-scroll silently.
    if (page === lastReportedPageRef.current && page === prevPageRef.current) {
      if (stickyTargetRef.current === page) {
        const slot = pageSlotRefs.current.get(page);
        if (slot) {
          const containerRect = container.getBoundingClientRect();
          const slotRect = slot.getBoundingClientRect();
          const drift = Math.abs(slotRect.top - containerRect.top);
          if (drift > 10) {
            // Layout shifted — silently correct scroll position
            container.scrollTo({ top: slot.offsetTop, left: computeCentreScrollLeft(page) ?? container.scrollLeft, behavior: 'instant' });
          }
        }
      }
      return;
    }

    if (page === prevPageRef.current) return;

    const prevPage = prevPageRef.current;
    prevPageRef.current = page;

    // Update lastReportedPageRef BEFORE scrolling so any observer callback
    // that fires during the transition sees the new target page and doesn't
    // echo the old page back to the parent.
    lastReportedPageRef.current = page;

    // Decide scroll strategy: instant jump for large page distances (citation
    // navigation), smooth scroll for small ones (prev/next buttons). Instant
    // jumps avoid the cascade of side-effects (auto-rotate detection, dimension
    // recomputation, centring) that fire when hundreds of pages fly through the
    // viewport during a smooth scroll.
    const distance = Math.abs(page - prevPage);
    const useInstantJump = distance > 5;

    // Mark sticky target for instant jumps so dimension-change re-scrolls
    // keep us pinned to this page. Cleared on manual scroll via the observer.
    stickyTargetRef.current = useInstantJump ? page : null;

    // Belt-and-braces suppression: the observer effect re-runs whenever page
    // changes (renderPage identity depends on page), which creates a fresh
    // IntersectionObserver whose initial notification may fire AFTER scrollend
    // releases programmaticScrollRef, carrying the pre-scroll topmost page.
    // A timestamp-based window survives observer teardown/re-creation races.
    programmaticScrollRef.current = true;
    suppressObserverUntilRef.current = Date.now() + 1000;

    const performScroll = async () => {
      if (useInstantJump) {
        // For large jumps, fetch real dimensions for ALL preceding pages so
        // slot.offsetTop is accurate instead of based on estimated averages.
        await ensureRealDimensionsUpTo(page);
      } else {
        // For small jumps, only pre-fetch the target page's dimensions.
        await ensureRealDimension(page);
      }
      // Bail if the user navigated to another page while we awaited.
      if (targetPageRef.current !== page) return;
      // Allow React to commit the new dimensions into slot CSS.
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (targetPageRef.current !== page) return;

      const slot = pageSlotRefs.current.get(page);
      if (!slot) return;

      const targetTop = slot.offsetTop;
      const targetLeft = computeCentreScrollLeft(page) ?? container.scrollLeft;
      container.scrollTo({ top: targetTop, left: targetLeft, behavior: useInstantJump ? 'instant' : 'smooth' });

      if (useInstantJump) {
        // Release suppression on next frame once layout settles.
        requestAnimationFrame(() => {
          programmaticScrollRef.current = false;
          centreHorizontallyOn(page);
          if (deferredAutoRotateRef.current.size > 0) {
            const deferred = new Set(deferredAutoRotateRef.current);
            deferredAutoRotateRef.current.clear();
            detectAutoRotateForPages(deferred);
          }
        });
      }
    };

    performScroll();

    if (!useInstantJump) {
      // Smooth scroll: use scrollend event to detect when animation finishes,
      // with a generous fallback timeout for browsers without support.
      const flushDeferredAutoRotate = () => {
        if (deferredAutoRotateRef.current.size > 0) {
          const deferred = new Set(deferredAutoRotateRef.current);
          deferredAutoRotateRef.current.clear();
          detectAutoRotateForPages(deferred);
        }
      };

      const onScrollEnd = () => {
        programmaticScrollRef.current = false;
        container.removeEventListener('scrollend', onScrollEnd);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        if (targetPageRef.current === page) centreHorizontallyOn(page);
        flushDeferredAutoRotate();
      };
      container.addEventListener('scrollend', onScrollEnd, { once: true });

      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        programmaticScrollRef.current = false;
        container.removeEventListener('scrollend', onScrollEnd);
        if (targetPageRef.current === page) centreHorizontallyOn(page);
        flushDeferredAutoRotate();
      }, 3000);
    }
  }, [page, pageDimensions, ensureRealDimension, ensureRealDimensionsUpTo, computeCentreScrollLeft, centreHorizontallyOn, detectAutoRotateForPages]);

  // ── Re-centre when the inner column's size changes ───────────────────────
  // The slot wrapper uses `min-width: min-content`, so when a wide page later
  // in the document renders (e.g. an A3 scan on the last page), the column
  // grows to that page's width. Narrow slots — including whichever page the
  // user is currently viewing — are then centred by `mx-auto` inside the
  // wider column, pushing them out of the visible 0-clientWidth viewport.
  // A ResizeObserver on the inner column re-centres the active page whenever
  // its size changes, so the cited content stays on screen no matter when
  // other pages' real dimensions land.
  const hasDims = pageDimensions.length > 0;
  useEffect(() => {
    if (!hasDims) return;
    const inner = innerColumnRef.current;
    if (!inner) return;
    let prevWidth = inner.scrollWidth;
    const ro = new ResizeObserver(() => {
      // Only act on actual width changes (height changes happen frequently
      // during canvas rendering and don't affect horizontal centring).
      const newWidth = inner.scrollWidth;
      if (newWidth === prevWidth) return;
      prevWidth = newWidth;
      // Defer to next frame so the new layout is committed before we measure.
      // Skip during programmatic scroll — setting scrollLeft directly cancels
      // the in-flight smooth scroll in Chrome/Safari.
      requestAnimationFrame(() => {
        if (programmaticScrollRef.current) return;
        const activePage = lastReportedPageRef.current;
        if (activePage >= 1) centreHorizontallyOn(activePage);
      });
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [hasDims, centreHorizontallyOn]);

  // Initial scroll to target page (no animation)
  const initialScrollDone = useRef(false);
  const prevDocumentUrlRef = useRef(documentUrl);
  if (prevDocumentUrlRef.current !== documentUrl) {
    prevDocumentUrlRef.current = documentUrl;
    initialScrollDone.current = false;
  }
  useEffect(() => {
    if (pageDimensions.length === 0) return;
    if (initialScrollDone.current) return;
    initialScrollDone.current = true;

    const container = containerRef.current;
    if (!container) return;

    const performInitialScroll = async () => {
      if (page > 1) {
        programmaticScrollRef.current = true;
        suppressObserverUntilRef.current = Date.now() + 1000;
        lastReportedPageRef.current = page;
      }

      // Pre-fetch the real dimension for the target page so the slot's CSS
      // width matches the real viewport before we measure for centring.
      await ensureRealDimension(page);
      if (targetPageRef.current !== page) return;
      // Allow React to commit the new dimension into the slot's CSS width.
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (targetPageRef.current !== page) return;

      const slot = pageSlotRefs.current.get(page);
      if (!slot) return;

      const targetTop = page > 1 ? slot.offsetTop : 0;
      const targetLeft = computeCentreScrollLeft(page) ?? container.scrollLeft;
      container.scrollTo({ top: targetTop, left: targetLeft, behavior: 'instant' });

      if (page > 1) {
        // Release observer suppression on the next frame, after layout settles.
        requestAnimationFrame(() => {
          programmaticScrollRef.current = false;
          // Idempotent re-centre once layout has fully settled.
          centreHorizontallyOn(page);
        });
      }
    };

    performInitialScroll();
  }, [pageDimensions, page, ensureRealDimension, computeCentreScrollLeft, centreHorizontallyOn]);

  // ── Re-centre when the active page's real width arrives via backfill ──────
  // For PDFs > INITIAL_DIM_BATCH pages, slots beyond the first batch start
  // with an estimated width. If the user navigated to such a page before its
  // real dimension was fetched, the initial centring used the estimate. When
  // the real value lands (via ensureRealDimension or the backfill loop), the
  // slot's CSS width changes — we must re-centre to keep the page visible.
  const activePageWidthRef = useRef<number | null>(null);
  useEffect(() => {
    const dim = pageDimensions[page - 1];
    if (!dim) {
      activePageWidthRef.current = null;
      return;
    }
    if (activePageWidthRef.current !== null && activePageWidthRef.current !== dim.width) {
      // Width changed for the currently displayed page — re-centre on next paint.
      // Skip during programmatic scroll to avoid cancelling smooth navigation.
      requestAnimationFrame(() => {
        if (programmaticScrollRef.current) return;
        if (targetPageRef.current === page) centreHorizontallyOn(page);
      });
    }
    activePageWidthRef.current = dim.width;
  }, [pageDimensions, page, centreHorizontallyOn]);

  // ── Search highlighting helpers ────────────────────────────────────────────
  const highlightOnCanvas = (
    canvas: HTMLCanvasElement,
    textContent: any,
    viewport: any,
    searchStr: string,
  ): { found: boolean; matchScore: number } => {
    const result = highlightOnCanvasUtil(canvas, textContent, viewport, searchStr);

    // Scroll so the highlight is visible within the scroll container (only once per unique highlight)
    const textKey = `text:${page}:${searchStr.substring(0, 30)}`;
    if (result.firstHighlightY !== null && containerRef.current && scrolledToHighlightRef.current !== textKey) {
      scrolledToHighlightRef.current = textKey;
      const slot = pageSlotRefs.current.get(page);
      if (slot) {
        const highlightY = result.firstHighlightY;
        setTimeout(() => {
          const c = containerRef.current;
          const s = pageSlotRefs.current.get(page);
          if (!c || !s) return;
          let centredLeft = c.scrollLeft;
          if (c.scrollWidth > c.clientWidth) {
            centredLeft = Math.max(0, s.offsetLeft + s.offsetWidth / 2 - c.clientWidth / 2);
          }
          c.scrollTo({
            top: Math.max(0, s.offsetTop + highlightY - 200),
            left: centredLeft,
            behavior: 'instant',
          });
        }, 50);
      }
    }

    return { found: result.found, matchScore: result.matchScore };
  };

  const tryAdjacentPages = async (
    pdf: pdfjsLib.PDFDocumentProxy,
    currentPage: number,
    searchStr: string,
  ) => {
    const searchNorm = textMatchNormalize(searchStr);
    if (searchNorm.length < 3) return;

    const isShortKeyword = searchNorm.length < 40;
    // Search nearby pages first, then widen. For very large documents (500+
    // pages), cap the total search to avoid freezing the tab — it's better
    // to miss a distant match than to hang.
    const nearRange = isShortKeyword ? 8 : 3;
    const MAX_PAGES_TO_SEARCH = 80;

    const pagesToTry: number[] = [];
    // Near pages first (interleaved ±1, ±2, ...)
    for (let offset = 1; offset <= nearRange; offset++) {
      pagesToTry.push(currentPage - offset, currentPage + offset);
    }
    // Then remaining pages (farther away), up to the cap
    for (let p = 1; p <= pdf.numPages && pagesToTry.length < MAX_PAGES_TO_SEARCH; p++) {
      if (p !== currentPage && !pagesToTry.includes(p)) {
        pagesToTry.push(p);
      }
    }

    let bestPage = -1;
    let bestScore = 0;

    for (const tryPage of pagesToTry) {
      if (tryPage < 1 || tryPage > pdf.numPages) continue;
      try {
        const pdfPage = await pdf.getPage(tryPage);
        const textContent = await pdfPage.getTextContent();
        const pageText = textMatchNormalize(
          textContent.items.map((item: any) => ('str' in item ? item.str : '')).join(' ')
        );

        // Try prefix first (fast path)
        const prefix = isShortKeyword ? searchNorm : searchNorm.substring(0, Math.min(100, searchNorm.length));
        if (pageText.includes(prefix)) {
          onPageCorrectionRef.current?.(tryPage);
          return;
        }

        // LCS fallback for paraphrased citations
        const score = textMatchLcsRatio(searchNorm, pageText);
        if (score > bestScore) {
          bestScore = score;
          bestPage = tryPage;
        }
      } catch { /* ignore */ }
    }

    // Accept LCS match if substantially better than what the original page scored
    if (bestScore >= 0.35 && bestPage > 0) {
      onPageCorrectionRef.current?.(bestPage);
    }
  };

  // ── Ref callback for page slots ────────────────────────────────────────────
  const setPageSlotRef = useCallback((pageNum: number, el: HTMLDivElement | null) => {
    if (el) pageSlotRefs.current.set(pageNum, el);
    else pageSlotRefs.current.delete(pageNum);
  }, []);

  const setCanvasRef = useCallback((pageNum: number, el: HTMLCanvasElement | null) => {
    if (el) canvasRefs.current.set(pageNum, el);
    else canvasRefs.current.delete(pageNum);
  }, []);

  const setHighlightCanvasRef = useCallback((pageNum: number, el: HTMLCanvasElement | null) => {
    if (el) highlightCanvasRefs.current.set(pageNum, el);
    else highlightCanvasRefs.current.delete(pageNum);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={`overflow-auto bg-medical-100 dark:bg-medical-800 relative ${className}`}
      style={{ scrollBehavior: 'auto', ...(areaZoomActive ? { cursor: 'crosshair' } : undefined) }}
      onMouseDown={handleSelectionMouseDown}
      onMouseMove={handleSelectionMouseMove}
      onMouseUp={handleSelectionMouseUp}
      onMouseLeave={() => { if (isDraggingRef.current) { isDraggingRef.current = false; setSelectionRect(null); } }}
    >
      {loading && (
        <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-3">
          <div className="flex items-center">
            <Loader className="h-8 w-8 animate-spin text-primary-600" />
            <span className="ml-2 text-medical-600 dark:text-medical-400">
              {downloadProgress !== null ? 'Downloading document...' : 'Loading document...'}
            </span>
          </div>
          {downloadProgress !== null && (
            <div className="w-48">
              <div className="h-2 bg-medical-200 dark:bg-medical-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-200"
                  style={{ width: `${Math.round(downloadProgress * 100)}%` }}
                />
              </div>
              <p className="text-xs text-medical-500 dark:text-medical-400 text-center mt-1">
                {Math.round(downloadProgress * 100)}%
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center h-full min-h-[200px] p-6">
          <div className="max-w-md text-center space-y-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setLoading(true);
                setRetryCount((n) => n + 1);
              }}
              className="px-4 py-2 rounded bg-medical-600 text-white hover:bg-medical-700 dark:bg-medical-500 dark:hover:bg-medical-400 text-sm font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {!loading && !error && pageDimensions.length > 0 && (
        <div ref={innerColumnRef} className="flex flex-col py-4" style={{ minWidth: 'min-content' }}>
          {pageDimensions.map((dim, idx) => {
            const pageNum = idx + 1;
            return (
              <div
                key={pageNum}
                ref={(el) => setPageSlotRef(pageNum, el)}
                data-page={pageNum}
                className="relative shadow-lg bg-white mx-auto"
                style={{
                  width: dim.width,
                  height: dim.height,
                  marginBottom: pageNum < pageDimensions.length ? PAGE_GAP : 0,
                  filter: cssFilterStyle,
                }}
              >
                <canvas
                  ref={(el) => setCanvasRef(pageNum, el)}
                  className="block"
                />
                <canvas
                  ref={(el) => setHighlightCanvasRef(pageNum, el)}
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{ mixBlendMode: 'multiply' }}
                />
                {redactionEnabled && !sourceDocsRevealed && piiRectsPerPage.get(pageNum)?.map((rect, i) => (
                  <PdfPiiBlur
                    key={`pii-${pageNum}-${i}`}
                    rect={rect}
                    viewportWidth={dim.width}
                    viewportHeight={dim.height}
                  />
                ))}
                {selectionRect && selectionRect.pageNum === pageNum && (() => {
                  const left = Math.min(selectionRect.startX, selectionRect.endX);
                  const top = Math.min(selectionRect.startY, selectionRect.endY);
                  const width = Math.abs(selectionRect.endX - selectionRect.startX);
                  const height = Math.abs(selectionRect.endY - selectionRect.startY);
                  return (
                    <div
                      className="absolute border-2 border-primary-500 bg-primary-500/10 z-[2] pointer-events-none"
                      style={{ left, top, width, height }}
                    />
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

// ── PII blur overlay for individual PDF regions ──────────────────────────────

interface PdfPiiBlurProps {
  rect: PiiRect;
  viewportWidth: number;
  viewportHeight: number;
}

const PdfPiiBlur: React.FC<PdfPiiBlurProps> = ({ rect, viewportWidth, viewportHeight }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <div
      className={`absolute transition-all duration-200 rounded-sm z-[1] ${
        revealed ? '' : 'select-none bg-amber-100/30 dark:bg-amber-900/20'
      }`}
      style={{
        left: rect.x * viewportWidth,
        top: rect.y * viewportHeight,
        width: rect.width * viewportWidth,
        height: rect.height * viewportHeight,
        backdropFilter: revealed ? 'none' : 'blur(5px)',
        WebkitBackdropFilter: revealed ? 'none' : 'blur(5px)',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onClick={() => setRevealed(prev => !prev)}
    />
  );
};
